import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert,
} from '@mui/material';
import { portalApi } from '../../services/portalApiClient';
import { API_ENDPOINTS } from '../../config/api';

interface Invoice {
  id: number;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  amount_paid: number;
  balance_due: number;
  status: string;
  payment_method: string;
  pet_name: string;
}

const statusColor = (s: string): 'success' | 'warning' | 'error' | 'default' => {
  if (s === 'Paid') return 'success';
  if (s === 'Partially Paid') return 'warning';
  if (s === 'Overdue') return 'error';
  return 'default';
};

const PortalInvoicesPage = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    portalApi.get<{ data: Invoice[] }>(API_ENDPOINTS.PORTAL.INVOICES)
      .then(res => setInvoices(Array.isArray(res.data) ? res.data : []))
      .catch(() => setError('Failed to load invoices'))
      .finally(() => setLoading(false));
  }, []);

  const totalOutstanding = invoices.reduce((s, i) => s + (i.balance_due ?? 0), 0);

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>My Invoices</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {totalOutstanding > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have an outstanding balance of <strong>${totalOutstanding.toFixed(2)}</strong>. Please contact the clinic to make a payment.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Invoice #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Pet</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Paid</TableCell>
              <TableCell align="right">Balance Due</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map(inv => (
              <TableRow key={inv.id} hover>
                <TableCell><strong>{inv.invoice_number || `#${inv.id}`}</strong></TableCell>
                <TableCell>{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '-'}</TableCell>
                <TableCell>{inv.pet_name || '-'}</TableCell>
                <TableCell align="right">${(inv.total_amount ?? 0).toFixed(2)}</TableCell>
                <TableCell align="right">${(inv.amount_paid ?? 0).toFixed(2)}</TableCell>
                <TableCell align="right">
                  <Typography color={(inv.balance_due ?? 0) > 0 ? 'error.main' : 'text.primary'}>
                    ${(inv.balance_due ?? 0).toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell><Chip label={inv.status} size="small" color={statusColor(inv.status)} /></TableCell>
              </TableRow>
            ))}
            {invoices.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center">No invoices found</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PortalInvoicesPage;
